import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Table, Button, Input, InputNumber, Select, message, Space, Popconfirm, Card, Upload } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api from '../utils/api';
import { getDataForms } from '../utils/dataForms';
import YearInput from './shared/YearInput';

// Get data forms for the department
const getDataFormsForDepartment = (departmentName, allDataForms) => {
  if (!departmentName || !allDataForms) return [];
  return allDataForms;
};

const EditableTargetTable = ({ department, targetRecords, onRefresh, dataForms }) => {
  const [dataSource, setDataSource] = useState([]);
  const [editingKey, setEditingKey] = useState('');
  const [saving, setSaving] = useState(false);

  // 获取部门的数据表单（使用 useMemo 确保正确更新）- 必须在所有使用它的地方之前定义
  const departmentDataForms = useMemo(() => {
    if (department?.name) {
      return getDataForms(department.name);
    }
    return dataForms || [];
  }, [department?.name, dataForms]);

  // 获取字段定义
  const getFieldsForDataType = (dataType) => {
    if (!dataForms || !dataType) return [];
    const formConfig = dataForms.find(f => f.key === dataType);
    return formConfig?.fields || [];
  };

  // 初始化数据源
  useEffect(() => {
    if (targetRecords && targetRecords.length > 0) {
      const formattedData = targetRecords.map((target, index) => ({
        key: target.id || `target-${index}`,
        id: target.id,
        data_type: target.data_type,
        field_key: target.field_key,
        field_label: target.field_label,
        field_type: target.field_type || 'number',
        category_value: target.category_value || null,
        target_value: target.target_value,
        start_year: target.start_year,
        end_year: target.end_year,
        target_year: target.target_year, // 向后兼容
        isNew: !target.id
      }));
      setDataSource(formattedData);
    } else {
      setDataSource([]);
    }
  }, [targetRecords]);

  // 当数据类型改变时，自动设置字段类型（如果只有一种类型）
  useEffect(() => {
    if (editingKey) {
      setDataSource(prev => {
        const record = prev.find(item => item.key === editingKey);
        if (record && record.data_type) {
          const formConfig = departmentDataForms.find(f => f.key === record.data_type);
          if (formConfig) {
            const excludedKeys = ['year', '年份', '年', '获奖年度'];
            const numericFields = formConfig.fields?.filter(f => 
              f.type === 'number' && !excludedKeys.includes(f.key)
            ) || [];
            const categoryFields = formConfig.fields?.filter(f => 
              (f.type === 'select' || f.type === 'text') && !excludedKeys.includes(f.key)
            ) || [];
            
            // 如果只有一种字段类型，自动设置
            if (numericFields.length > 0 && categoryFields.length === 0 && record.field_type !== 'number') {
              return prev.map(item => 
                item.key === editingKey 
                  ? { ...item, field_type: 'number', field_key: null, field_label: null, category_value: null }
                  : item
              );
            } else if (categoryFields.length > 0 && numericFields.length === 0 && record.field_type !== 'category') {
              return prev.map(item => 
                item.key === editingKey 
                  ? { ...item, field_type: 'category', field_key: null, field_label: null, category_value: null }
                  : item
              );
            }
          }
        }
        return prev;
      });
    }
  }, [editingKey, departmentDataForms]);

  // 创建空行
  const createEmptyRow = () => ({
    key: `new-${Date.now()}-${Math.random()}`,
    isNew: true,
    data_type: null,
    field_key: null,
    field_label: null,
    field_type: 'number',
    category_value: null,
    target_value: null,
    start_year: new Date().getFullYear(),
    end_year: new Date().getFullYear() + 1,
    target_year: new Date().getFullYear() + 1
  });

  // 开始编辑
  const startEdit = (record) => {
    setEditingKey(record.key);
  };

  // 取消编辑
  const cancelEdit = () => {
    setEditingKey('');
  };

  // 保存单行
  const saveRow = async (key) => {
    const row = dataSource.find(item => item.key === key);
    if (!row) return;

    // 验证必填字段
    if (!row.data_type) {
      message.error('请选择数据类型');
      return;
    }
    if (!row.field_key) {
      message.error('请选择字段');
      return;
    }
    if (row.target_value === null || row.target_value === undefined) {
      message.error('请输入目标值');
      return;
    }
    if (!row.start_year || !row.end_year) {
      message.error('请输入开始年份和结束年份');
      return;
    }

    setSaving(true);
    try {
      const targetData = {
        department_id: department.id,
        data_type: row.data_type,
        field_key: row.field_key,
        field_label: row.field_label,
        field_type: row.field_type || 'number',
        category_value: row.category_value || null,
        target_value: row.target_value,
        start_year: row.start_year,
        end_year: row.end_year,
        target_year: row.start_year || row.target_year // 向后兼容
      };

      if (row.id) {
        // 更新现有目标
        await api.put(`/indicator-targets/${row.id}`, {
          target_value: targetData.target_value,
          start_year: targetData.start_year,
          end_year: targetData.end_year,
          field_label: targetData.field_label,
          category_value: targetData.category_value
        });
        message.success('目标更新成功');
      } else {
        // 创建新目标
        await api.post('/indicator-targets', targetData);
        message.success('目标创建成功');
      }

      setEditingKey('');
      onRefresh && onRefresh();
    } catch (error) {
      console.error('保存目标失败:', error);
      message.error(error.response?.data?.error || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  // 删除行
  const deleteRow = async (key) => {
    const row = dataSource.find(item => item.key === key);
    if (!row || !row.id) {
      // 如果是新行，直接从数据源中移除
      setDataSource(prev => prev.filter(item => item.key !== key));
      return;
    }

    try {
      await api.delete(`/indicator-targets/${row.id}`);
      message.success('目标删除成功');
      onRefresh && onRefresh();
    } catch (error) {
      console.error('删除目标失败:', error);
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  // 添加新行
  const addRow = () => {
    const newRow = createEmptyRow();
    setDataSource(prev => [...prev, newRow]);
    setEditingKey(newRow.key);
  };

  // Excel 导出
  const handleExport = () => {
    if (dataSource.length === 0) {
      message.warning('没有数据可导出');
      return;
    }

    const exportData = dataSource.map(row => ({
      '数据类型': row.data_type || '',
      '字段标签': row.field_label || '',
      '字段类型': row.field_type === 'category' ? '分类字段' : '数值字段',
      '分类值': row.category_value || '',
      '目标值': row.target_value || '',
      '开始年份': row.start_year || '',
      '结束年份': row.end_year || '',
      '目标年份': row.target_year || ''
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '指标目标');
    XLSX.writeFile(wb, `指标目标_${department?.name || '导出'}_${new Date().toISOString().split('T')[0]}.xlsx`);
    message.success('导出成功');
  };

  // Excel 导入
  const handleImport = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);

        if (jsonData.length === 0) {
          message.error('Excel文件为空或格式不正确');
          return;
        }

        // 验证列名
        const requiredColumns = ['数据类型', '字段标签', '目标值', '开始年份', '结束年份'];
        const firstRow = jsonData[0];
        const missingColumns = requiredColumns.filter(col => !(col in firstRow));
        if (missingColumns.length > 0) {
          message.error(`Excel文件缺少必需的列: ${missingColumns.join(', ')}`);
          return;
        }

        // 转换数据
        const importedRows = jsonData.map((row, index) => {
          // 查找对应的数据类型
          const dataType = departmentDataForms.find(f => f.label === row['数据类型'])?.key || row['数据类型'];
          const formConfig = departmentDataForms.find(f => f.key === dataType);
          
          // 查找字段
          let fieldKey = null;
          let fieldLabel = row['字段标签'] || '';
          if (formConfig) {
            const field = formConfig.fields?.find(f => f.label === fieldLabel || f.key === fieldLabel);
            if (field) {
              fieldKey = field.key;
              fieldLabel = field.label;
            } else {
              // 如果找不到，尝试使用字段标签作为key
              fieldKey = fieldLabel;
            }
          } else {
            // 如果找不到表单配置，使用原始值
            fieldKey = fieldLabel;
          }

          return {
            key: `imported-${Date.now()}-${index}`,
            isNew: true,
            data_type: dataType,
            field_key: fieldKey,
            field_label: fieldLabel,
            field_type: row['字段类型'] === '分类字段' ? 'category' : 'number',
            category_value: row['分类值'] || null,
            target_value: parseFloat(row['目标值']) || 0,
            start_year: parseInt(row['开始年份']) || new Date().getFullYear(),
            end_year: parseInt(row['结束年份']) || new Date().getFullYear() + 1,
            target_year: parseInt(row['目标年份']) || parseInt(row['开始年份']) || new Date().getFullYear() + 1
          };
        });

        // 添加到数据源
        setDataSource(prev => [...prev, ...importedRows]);
        message.success(`成功导入 ${importedRows.length} 条数据`);
      } catch (error) {
        console.error('导入失败:', error);
        message.error('导入失败: ' + (error.message || '文件格式不正确'));
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // 阻止默认上传行为
  };

  // 下载模板
  const handleDownloadTemplate = () => {
    const templateData = [{
      '数据类型': '示例：基本条件数据',
      '字段标签': '示例：专任教师总人数',
      '字段类型': '数值字段',
      '分类值': '',
      '目标值': 100,
      '开始年份': 2024,
      '结束年份': 2025,
      '目标年份': 2024
    }];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '指标目标模板');
    XLSX.writeFile(wb, '指标目标导入模板.xlsx');
    message.success('模板下载成功');
  };

  // 构建表格列
  const buildColumns = () => {
    const columns = [
      {
        title: '数据类型',
        dataIndex: 'data_type',
        key: 'data_type',
        width: 150,
        render: (text, record) => {
          if (editingKey === record.key) {
            return (
              <Select
                value={text}
                onChange={(value) => {
                  const formConfig = departmentDataForms.find(f => f.key === value);
                  // 检查该数据类型有哪些可用的字段类型
                  const excludedKeys = ['year', '年份', '年', '获奖年度'];
                  const numericFields = formConfig?.fields?.filter(f => 
                    f.type === 'number' && !excludedKeys.includes(f.key)
                  ) || [];
                  const categoryFields = formConfig?.fields?.filter(f => 
                    (f.type === 'select' || f.type === 'text') && !excludedKeys.includes(f.key)
                  ) || [];
                  
                  // 确定字段类型
                  let fieldType = record.field_type;
                  if (numericFields.length > 0 && categoryFields.length === 0) {
                    // 只有数值字段
                    fieldType = 'number';
                  } else if (categoryFields.length > 0 && numericFields.length === 0) {
                    // 只有分类字段
                    fieldType = 'category';
                  } else {
                    // 两种都有，保持当前选择或默认为 number
                    fieldType = record.field_type || 'number';
                  }
                  
                  setDataSource(prev => prev.map(item => 
                    item.key === record.key 
                      ? { ...item, data_type: value, field_type: fieldType, field_key: null, field_label: null, category_value: null }
                      : item
                  ));
                }}
                placeholder="选择数据类型"
                style={{ width: '100%' }}
              >
                {departmentDataForms.map(form => (
                  <Select.Option key={form.key} value={form.key}>
                    {form.label}
                  </Select.Option>
                ))}
              </Select>
            );
          }
          const formConfig = departmentDataForms.find(f => f.key === text);
          return formConfig?.label || text || '-';
        }
      },
      {
        title: '字段标签',
        dataIndex: 'field_label',
        key: 'field_label',
        width: 200,
        render: (text, record) => {
          if (editingKey === record.key) {
            const formConfig = departmentDataForms.find(f => f.key === record.data_type);
            // 数值字段：包含 computed 字段（如总人数），但排除年份相关字段
            // 排除：'year', '年份', '年', '获奖年度' 等无意义的统计字段
            const excludedKeys = ['year', '年份', '年', '获奖年度'];
            const numericFields = formConfig?.fields?.filter(f => 
              f.type === 'number' && !excludedKeys.includes(f.key)
            ) || [];
            // 分类字段：排除年份相关字段，但可以包含其他分类字段（包括 computed 的）
            const categoryFields = formConfig?.fields?.filter(f => 
              (f.type === 'select' || f.type === 'text') && !excludedKeys.includes(f.key)
            ) || [];
            
            const fields = record.field_type === 'category' ? categoryFields : numericFields;
            
            return (
              <Select
                value={record.field_key}
                onChange={(value) => {
                  const field = fields.find(f => f.key === value);
                  setDataSource(prev => prev.map(item => 
                    item.key === record.key 
                      ? { 
                          ...item, 
                          field_key: value, 
                          field_label: field?.label || '',
                          category_value: record.field_type === 'category' ? item.category_value : null
                        }
                      : item
                  ));
                }}
                placeholder="选择字段"
                style={{ width: '100%' }}
              >
                {fields.map(field => (
                  <Select.Option key={field.key} value={field.key}>
                    {field.label}
                  </Select.Option>
                ))}
              </Select>
            );
          }
          return text || '-';
        }
      },
      {
        title: '字段类型',
        dataIndex: 'field_type',
        key: 'field_type',
        width: 120,
        render: (text, record) => {
          if (editingKey === record.key) {
            const formConfig = departmentDataForms.find(f => f.key === record.data_type);
            if (!formConfig) {
              return text === 'category' ? '分类字段' : '数值字段';
            }
            
            // 检查该数据类型有哪些可用的字段类型
            const excludedKeys = ['year', '年份', '年', '获奖年度'];
            const numericFields = formConfig?.fields?.filter(f => 
              f.type === 'number' && !excludedKeys.includes(f.key)
            ) || [];
            const categoryFields = formConfig?.fields?.filter(f => 
              (f.type === 'select' || f.type === 'text') && !excludedKeys.includes(f.key)
            ) || [];
            
            // 如果只有一种字段类型，隐藏选择器，直接显示文本
            if (numericFields.length > 0 && categoryFields.length === 0) {
              // 只有数值字段，直接显示
              return '数值字段';
            } else if (categoryFields.length > 0 && numericFields.length === 0) {
              // 只有分类字段，直接显示
              return '分类字段';
            } else {
              // 两种都有，显示选择器
              return (
                <Select
                  value={text}
                  onChange={(value) => {
                    setDataSource(prev => prev.map(item => 
                      item.key === record.key 
                        ? { ...item, field_type: value, field_key: null, field_label: null, category_value: null }
                        : item
                    ));
                  }}
                  style={{ width: '100%' }}
                >
                  <Select.Option value="number">数值字段</Select.Option>
                  <Select.Option value="category">分类字段</Select.Option>
                </Select>
              );
            }
          }
          return text === 'category' ? '分类字段' : '数值字段';
        }
      },
      {
        title: '分类值',
        dataIndex: 'category_value',
        key: 'category_value',
        width: 150,
        render: (text, record) => {
          if (editingKey === record.key && record.field_type === 'category') {
            const formConfig = departmentDataForms.find(f => f.key === record.data_type);
            const field = formConfig?.fields?.find(f => f.key === record.field_key);
            const options = field?.options || [];
            
            if (options.length > 0) {
              return (
                <Select
                  value={text}
                  onChange={(value) => {
                    setDataSource(prev => prev.map(item => 
                      item.key === record.key ? { ...item, category_value: value } : item
                    ));
                  }}
                  placeholder="选择分类值"
                  style={{ width: '100%' }}
                >
                  {options.map(opt => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
              );
            } else {
              return (
                <Input
                  value={text || ''}
                  onChange={(e) => {
                    setDataSource(prev => prev.map(item => 
                      item.key === record.key ? { ...item, category_value: e.target.value } : item
                    ));
                  }}
                  placeholder="输入分类值"
                />
              );
            }
          }
          return text || '-';
        }
      },
      {
        title: '目标值',
        dataIndex: 'target_value',
        key: 'target_value',
        width: 120,
        render: (text, record) => {
          if (editingKey === record.key) {
            return (
              <InputNumber
                value={text}
                onChange={(value) => {
                  setDataSource(prev => prev.map(item => 
                    item.key === record.key ? { ...item, target_value: value } : item
                  ));
                }}
                style={{ width: '100%' }}
                min={0}
                precision={2}
              />
            );
          }
          return text !== null && text !== undefined ? text : '-';
        }
      },
      {
        title: '开始年份',
        dataIndex: 'start_year',
        key: 'start_year',
        width: 120,
        render: (text, record) => {
          if (editingKey === record.key) {
            return (
              <YearInput
                value={text}
                onChange={(value) => {
                  setDataSource(prev => prev.map(item => 
                    item.key === record.key ? { ...item, start_year: value } : item
                  ));
                }}
                min={1900}
                max={2100}
              />
            );
          }
          return text || '-';
        }
      },
      {
        title: '结束年份',
        dataIndex: 'end_year',
        key: 'end_year',
        width: 120,
        render: (text, record) => {
          if (editingKey === record.key) {
            return (
              <YearInput
                value={text}
                onChange={(value) => {
                  setDataSource(prev => prev.map(item => 
                    item.key === record.key ? { ...item, end_year: value } : item
                  ));
                }}
                min={1900}
                max={2100}
              />
            );
          }
          return text || '-';
        }
      },
      {
        title: '操作',
        key: 'action',
        width: 150,
        fixed: 'right',
        render: (_, record) => {
          const isEditing = editingKey === record.key;
          if (isEditing) {
            return (
              <Space>
                <Button
                  type="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={() => saveRow(record.key)}
                  loading={saving}
                >
                  保存
                </Button>
                <Button
                  size="small"
                  onClick={cancelEdit}
                >
                  取消
                </Button>
              </Space>
            );
          }
          return (
            <Space>
              <Button
                type="link"
                size="small"
                onClick={() => startEdit(record)}
              >
                编辑
              </Button>
              <Popconfirm
                title="确定要删除这条目标吗？"
                onConfirm={() => deleteRow(record.key)}
              >
                <Button
                  type="link"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                >
                  删除
                </Button>
              </Popconfirm>
            </Space>
          );
        }
      }
    ];

    return columns;
  };

  // 添加安全检查（在所有 Hooks 之后）
  if (!department) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>加载中...</div>;
  }

  return (
    <div>
      <Card
        size="small"
        style={{ marginBottom: '16px' }}
        title="指标目标管理"
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
            >
              下载模板
            </Button>
            <Upload
              accept=".xlsx,.xls"
              beforeUpload={handleImport}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>
                导入Excel
              </Button>
            </Upload>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={dataSource.length === 0}
            >
              导出Excel
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={addRow}
            >
              添加目标
            </Button>
          </Space>
        }
      >
        <Table
          columns={buildColumns()}
          dataSource={dataSource}
          rowKey="key"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          size="middle"
          bordered
        />
      </Card>
    </div>
  );
};

export default EditableTargetTable;

